import { v4 as uuidv4 } from 'uuid';
import { Demographics, ExperimentConditions, ExperimentData, Participant } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Helper function to simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Random condition assignment - FTT-based only
const assignConditions = (): ExperimentConditions => {
  return {
    congruence: Math.random() > 0.5 ? 'congruent' : 'incongruent',
    taskOrder: Math.random() > 0.5 ? 'email-first' : 'device-first',
    timePressure: Math.random() > 0.5 ? 'high' : 'low'
  };
};

/**
 * Which flow new arrivals get: 'main' | 'calibration' | 'closed'. Server-side
 * switch (ACTIVE_ARM); a failure to reach the server falls back to 'main' so the
 * original study keeps working if the endpoint is ever unavailable.
 */
export const fetchActiveArm = async (): Promise<{ arm: string; mainStudy?: string; bankRevision?: string; cardRevision?: string }> => {
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/experiments/arm`, {}, 8000);
    if (!response.ok) throw new Error(`arm ${response.status}`);
    const json = await response.json();
    return { arm: String(json.arm || 'main'), mainStudy: json.mainStudy, bankRevision: json.bankRevision, cardRevision: json.cardRevision };
  } catch (e) {
    console.warn('Could not fetch active arm; defaulting to legacy main', e);
    return { arm: 'main', mainStudy: 'legacy' };
  }
};

export const initializeExperiment = async (demographics: Demographics): Promise<ExperimentData> => {
  try {
    console.log('Initializing experiment with API URL:', API_BASE_URL);
    console.log('Demographics:', demographics);
    
    // Get study number from URL parameter or default to 1
    const urlParams = new URLSearchParams(window.location.search);
    const studyNumber = parseInt(urlParams.get('study') || '1', 10);
    
    // consentStartTime is stamped when the consent screen first loads (App.tsx), so
    // the server can compute full in-app time (endTime - consentStartTime), which
    // includes consent + pre-survey that precede participant creation.
    const consentStartTime = parseInt(localStorage.getItem('consentStartTime') || '0', 10) || undefined;

    // Attach Prolific identifiers (captured at landing in App.tsx) so they persist on the
    // participant for payment reconciliation. Stored inside demographics, which the backend
    // persists wholesale (demographics.prolificId/urlProlificId already exist in the schema).
    const prolificId = localStorage.getItem('prolificId') || urlParams.get('PROLIFIC_PID') || urlParams.get('prolific_pid') || undefined;
    const demographicsWithProlific: any = prolificId ? {
      ...demographics,
      prolificId,
      urlProlificId: prolificId,
      prolificStudyId: localStorage.getItem('prolificStudyId') || urlParams.get('STUDY_ID') || undefined,
      prolificSessionId: localStorage.getItem('prolificSessionId') || urlParams.get('SESSION_ID') || undefined,
    } : demographics;

    const sonaId = localStorage.getItem('sonaId') || undefined;

    const requestBody = {
      demographics: sonaId ? { ...demographicsWithProlific, sonaId } : demographicsWithProlific,
      studyNumber,
      studyVersion: 'v2.0-simplified', // Track experiment version for data analysis
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      consentStartTime
    };
    console.log('Request body:', JSON.stringify(requestBody, null, 2));
    
    const response = await fetch(`${API_BASE_URL}/experiments/initialize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    });

    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(`Failed to initialize experiment: ${response.status} ${errorText}`);
    }

    const result = await response.json();
    
    if (result.success && result.data) {
      // Guarantee participantId is in localStorage BEFORE any tracking event can
      // fire. TrackerContext.sendBatch reads it from localStorage and silently
      // drops batches if it's missing — this is the single source of truth.
      if (result.data.participant?.id) {
        try {
          localStorage.setItem('participantId', result.data.participant.id);
          // Server-assigned item-class rotation (advisor R3): the task screens must use this
          // value, not a client hash. Kept beside the session so a refresh still finds it;
          // cleared with the rest of the session (?reset=1, Complete).
          const rot = result.data.participant.rotation;
          if (typeof rot === 'number' && Number.isInteger(rot)) localStorage.setItem('assignedRotation', String(rot));
          else localStorage.removeItem('assignedRotation');
          if (result.data.participant.mainArmInterface) localStorage.setItem('mainArmInterface', result.data.participant.mainArmInterface);
        } catch (e) {
          console.error('Could not persist participantId to localStorage:', e);
        }
      }

      const experimentData: ExperimentData = {
        participant: result.data.participant,
        startTime: new Date(result.data.startTime).getTime(),
        completed: false
      };
      return experimentData;
    } else {
      throw new Error('Invalid response from server');
    }
  } catch (error) {
    console.error('Failed to initialize experiment:', error);
    // Add more detailed error logging
    if (error instanceof Error) {
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
    }
    throw error;
  }
};

/**
 * Phases whose payload is the participant's whole cumulative state (the verification-closure
 * arms). A newer payload supersedes every older one, so (a) queued failures of the same phase
 * are dropped rather than resent, and (b) only the latest failure is kept.
 */
const CUMULATIVE_PHASES = new Set(['calibration-task', 'main-task']);

export const savePhaseData = async (participantId: string, phase: string, data: any): Promise<void> => {
  // First, attempt to resend any previously failed phase data for this participant
  const failedKey = `failedPhaseData_${participantId}`;
  try {
    const storedFailed = localStorage.getItem(failedKey);
    if (storedFailed) {
      const failedItems: Array<{ phase: string; data: any }> = (JSON.parse(storedFailed) as Array<{ phase: string; data: any }>)
        // the payload being sent now contains everything an older one of the same cumulative phase had
        .filter(item => !(CUMULATIVE_PHASES.has(phase) && item.phase === phase));
      const remaining: Array<{ phase: string; data: any }> = [];
      for (const item of failedItems) {
        try {
          const retryEndpoint = item.phase === 'post-experiment'
            ? `${API_BASE_URL}/experiments/${participantId}/survey/${item.phase}`
            : `${API_BASE_URL}/experiments/${participantId}/phases/${item.phase}`;
          const retryResp = await fetchWithTimeout(retryEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ phase: item.phase, data: item.data })
          });
          if (!retryResp.ok) remaining.push(item);
        } catch {
          remaining.push(item);
        }
      }
      if (remaining.length === 0) {
        localStorage.removeItem(failedKey);
      } else {
        localStorage.setItem(failedKey, JSON.stringify(remaining));
      }
    }
  } catch (e) {
    // localStorage access can fail in private browsing; ignore
  }

  try {
    console.log('Saving phase data:', { participantId, phase });

    // Use the flexible survey endpoint for post-experiment data, regular phases endpoint for others
    const endpoint = phase === 'post-experiment'
      ? `${API_BASE_URL}/experiments/${participantId}/survey/${phase}`
      : `${API_BASE_URL}/experiments/${participantId}/phases/${phase}`;

    console.log('API endpoint:', endpoint);

    const response = await fetchWithTimeout(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phase, data })
    });

    console.log('Response status:', response.status, 'Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server error response:', errorText);
      throw new Error(`Failed to save phase data: ${response.status} - ${errorText}`);
    }

    const responseData = await response.json();
    console.log('Phase data saved successfully:', { participantId, phase, responseData });
  } catch (error) {
    console.error('Failed to save phase data:', error);
    // Store failed data in localStorage so it can be retried on next call
    try {
      const existing = localStorage.getItem(failedKey);
      const failedItems: Array<{ phase: string; data: any }> = (existing ? JSON.parse(existing) as Array<{ phase: string; data: any }> : [])
        .filter(item => !(CUMULATIVE_PHASES.has(phase) && item.phase === phase)); // keep only the latest cumulative state
      failedItems.push({ phase, data });
      localStorage.setItem(failedKey, JSON.stringify(failedItems));
      console.warn('Phase data stored in localStorage for retry:', { participantId, phase });
    } catch (storageError) {
      // localStorage may be full or unavailable; nothing more we can do
    }
    throw error;
  }
};

/**
 * Coalescing save for cumulative phases (Nov 2 load test). The screens persist the whole
 * state at every step without waiting, which put several overlapping saves for one student
 * on the server at once; a slow older save could then land after a newer one. Here at most
 * one save per participant+phase is in flight. A call made while one is running replaces the
 * waiting payload (the newest state contains every older one), and its promise settles when
 * a save that includes its state has finished. Order is preserved; nothing is dropped.
 */
type CumState = { participantId: string; phase: string; running: boolean; pending: any; waiters: Array<{ resolve: () => void; reject: (e: unknown) => void }> };
const cumulative: Record<string, CumState> = {};
export const saveCumulative = (participantId: string, phase: string, data: any): Promise<void> => {
  const key = `${participantId}|${phase}`;
  const s = cumulative[key] || (cumulative[key] = { participantId, phase, running: false, pending: null, waiters: [] });
  s.pending = data;
  const settled = new Promise<void>((resolve, reject) => s.waiters.push({ resolve, reject }));
  if (!s.running) void drainCumulative(s);
  return settled;
};
async function drainCumulative(s: CumState) {
  s.running = true;
  try {
    while (s.pending !== null) {
      const data = s.pending; const waiters = s.waiters;
      s.pending = null; s.waiters = [];
      try { await savePhaseData(s.participantId, s.phase, data); waiters.forEach(w => w.resolve()); }
      catch (e) { waiters.forEach(w => w.reject(e)); }
    }
  } finally { s.running = false; }
}

/** fetch with a hard timeout: a dead server must fail fast so retry/queue logic runs, not hang. */
export const fetchWithTimeout = (input: RequestInfo, init: RequestInit = {}, ms = 15000): Promise<Response> => {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  return fetch(input, { ...init, signal: ctrl.signal }).finally(() => clearTimeout(timer));
};

export const completeExperiment = async (participantId: string, data: any): Promise<any> => {
  try {
    // Backend reads req.body.data — wrap the payload so debrief acknowledgments/
    // feedback/performance actually persist (previously sent unwrapped → dropped).
    const response = await fetchWithTimeout(`${API_BASE_URL}/experiments/${participantId}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data })
    });

    if (!response.ok) {
      throw new Error(`Failed to complete experiment: ${response.statusText}`);
    }

    // Return the parsed body — it carries the Prolific completionUrl/code, which
    // the server releases only after validating the data (not baked into the JS).
    const result = await response.json().catch(() => ({}));
    console.log('Experiment completed:', { participantId });
    return result;
  } catch (error) {
    console.error('Failed to complete experiment:', error);
    throw error;
  }
};

export const trackBehavior = async (participantId: string, behaviorData: any): Promise<void> => {
  try {
    const response = await fetch(`${API_BASE_URL}/tracking/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        participantId,
        events: Array.isArray(behaviorData) ? behaviorData : [behaviorData]
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to track behavior: ${response.statusText}`);
    }
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(behaviorData)
    // });

    console.log('Behavior tracked:', { participantId, behaviorData });
  } catch (error) {
    console.error('Failed to track behavior:', error);
    // Don't throw here - we don't want tracking failures to break the experiment
  }
};